/**
 * The day's public `Date?` / `Time?` badge count, on the Operations → Reports page (#67).
 *
 * One compact line, counts only: the reasons behind the badges live in the API and the pipeline telemetry,
 * and an operator should not have to read reason codes here. Renders nothing until a snapshot exists.
 */

/** The `dateQuality` value `GET /api/mgmt/reports/daily` returns (mirrors `lib/date-quality-snapshot.ts`). */
export interface DateQualitySnapshot {
  /** When the snapshot was taken (ISO). */
  takenAt: string;
  /** The recorded counts (`upcoming`, `publicAny`, `publicDate`, `publicTime`, …). */
  extras: Record<string, string | number>;
}

function count(extras: Record<string, string | number>, key: string): number | null {
  const value = extras[key];
  const parsed = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** `takenAt` as a local date, or '' when it does not parse. */
function localDate(iso: string): string {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleDateString();
}

/**
 * The line's text, or null when there is no snapshot (or it carries no usable counts).
 * Exported so the copy can be checked without rendering.
 */
export function dateQualityLine(snapshot: DateQualitySnapshot | null | undefined): string | null {
  const extras = snapshot?.extras;
  if (!snapshot || !extras) return null;
  const upcoming = count(extras, 'upcoming');
  const publicAny = count(extras, 'publicAny');
  const publicDate = count(extras, 'publicDate');
  const publicTime = count(extras, 'publicTime');
  if (upcoming === null || publicAny === null || publicDate === null || publicTime === null) return null;

  const asOf = localDate(snapshot.takenAt);
  const head = `Public date/time badges: ${publicAny.toLocaleString()} of ${upcoming.toLocaleString()} upcoming`
    + ` (${publicDate.toLocaleString()} date, ${publicTime.toLocaleString()} time)`;
  return asOf ? `${head} · as of ${asOf}` : head;
}

export default function DateQualitySummary({
  snapshot
}: {
  snapshot: DateQualitySnapshot | null | undefined;
}) {
  const line = dateQualityLine(snapshot);
  if (!line) return null;
  return <p className="small text-body-secondary mb-3">{line}</p>;
}
